import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface AuthContextType {
  user: any;
  profile: any;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
}

interface Profile {
  id: string;
  email: string;
  student_name: string | null;
  tutor_name: string | null;
  whatsapp: string | null;
  trial_used: boolean;
  trial_expired: boolean;
  trial_start_date: string | null;
  subscription_status: 'trial' | 'active' | 'inactive';
  subscription_end_date: string | null;
  total_points: number;
  level: number;
  next_level_points: number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Verificar sesión actual
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      }
      setLoading(false);
    });

    // Escuchar cambios en la autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;

      // Verificar el estado de la suscripción
      const { data: subscriptionData } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .single();

      // Actualizar el estado de la suscripción en el perfil
      const updatedProfile = {
        ...data,
        subscription_status: subscriptionData ? 'active' : data.subscription_status,
        subscription_end_date: subscriptionData?.current_period_end || null
      };

      setProfile(updatedProfile);

      // Solo mostrar mensaje de expiración si no hay suscripción activa y el trial expiró
      if (data.trial_expired && !subscriptionData) {
        toast.error('Tu período de prueba ha expirado. Por favor, actualiza a Premium para continuar.');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      navigate('/');
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { error: signUpError, data } = await supabase.auth.signUp({
        email,
        password,
      });

      if (signUpError) throw signUpError;

      if (data.user) {
        // Crear perfil con período de prueba iniciado
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: data.user.id,
            trial_used: true,
            trial_expired: false,
            trial_start_date: new Date().toISOString(),
            subscription_status: 'trial'
          });

        if (profileError) throw profileError;
      }

      toast.success('¡Registro exitoso! Por favor, verifica tu correo electrónico.');
      navigate('/');
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setProfile(null);
      navigate('/');
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, signIn, signUp, signOut, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
